import { useState } from "react";
import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BrandLogo from "../BrandLogo";
import LanguageSwitcher from "../language";
import { useCustomColors } from "../../hooks/useCustomColors";
import SupportContact from "../SupportContact";
import { AuthMode } from "./AuthPanel";
import { brand } from "../../theme";

interface AuthNavDrawerProps {
  mode: AuthMode;
  onOpen: (mode: AuthMode) => void;
}

const ACTIONS: { mode: AuthMode; labelKey: string }[] = [
  { mode: "login", labelKey: "login" },
  { mode: "signup", labelKey: "sign_up" },
];

/** Side nav for the signed-out pages. The header keeps only the menu button;
 *  Login / Sign Up live inside the drawer. */
const AuthNavDrawer = ({ mode, onOpen }: AuthNavDrawerProps) => {
  const colors = useCustomColors();
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const choose = (next: AuthMode) => {
    setOpen(false);
    onOpen(next);
  };

  const actionSx = (actionMode: AuthMode) => {
    const isSignup = actionMode === "signup";

    return {
      height: "46px",
      borderRadius: "12px",
      overflow: "visible",
      color: isSignup ? brand.primarySoft : "#F8E9CF",
      border: isSignup
        ? `1px solid ${colors.accent.goldBright}`
        : `1px solid ${colors.border.strong}`,
      background: isSignup
        ? `linear-gradient(180deg, rgba(255, 255, 255, 0.38) 0%, rgba(255, 241, 184, 0.10) 34%, rgba(112, 72, 8, 0.18) 100%), ${colors.gradient.goldPill}`
        : `linear-gradient(180deg, ${colors.card.background} 0%, ${brand.primarySoft} 100%)`,
      boxShadow: isSignup
        ? "inset 0 1px 0 rgba(255, 255, 255, 0.82), inset 0 -2px 3px rgba(91, 48, 5, 0.30), 0 0 0 1px rgba(99, 25, 26, 0.24), 0 5px 16px rgba(212, 175, 55, 0.20)"
        : "inset 0 1px 0 rgba(255, 225, 155, 0.12), inset 0 -2px 5px rgba(0, 0, 0, 0.30), 0 4px 12px rgba(99, 25, 26, 0.18)",
      transition: "border-color 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease",
      "&:hover": {
        background: isSignup
          ? `linear-gradient(180deg, rgba(255, 255, 255, 0.52) 0%, rgba(255, 241, 184, 0.16) 34%, rgba(112, 72, 8, 0.14) 100%), ${colors.gradient.goldPill}`
          : `linear-gradient(180deg, ${brand.primarySoft} 0%, ${brand.primaryDark} 100%)`,
        borderColor: colors.accent.goldBright,
        boxShadow: isSignup
          ? "inset 0 1px 0 rgba(255, 255, 255, 0.92), inset 0 -2px 3px rgba(91, 48, 5, 0.24), 0 0 0 1px rgba(99, 25, 26, 0.28), 0 0 20px rgba(240, 201, 77, 0.36), 0 7px 18px rgba(0, 0, 0, 0.24)"
          : "inset 0 1px 0 rgba(255, 225, 155, 0.18), inset 0 -2px 5px rgba(0, 0, 0, 0.26), 0 0 16px rgba(212, 175, 55, 0.24), 0 6px 16px rgba(99, 25, 26, 0.26)",
        filter: "brightness(1.06)",
      },
      "&:active": {
        filter: "brightness(0.96)",
        boxShadow: isSignup
          ? "inset 0 2px 5px rgba(91, 48, 5, 0.28), 0 0 12px rgba(212, 175, 55, 0.20)"
          : "inset 0 2px 5px rgba(0, 0, 0, 0.38), 0 0 10px rgba(212, 175, 55, 0.14)",
      },
      "&.Mui-focusVisible": {
        outline: `3px solid ${isSignup ? colors.accent.primary : colors.accent.goldBright}`,
        outlineOffset: "3px",
      },
    };
  };

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        aria-label="menu"
        sx={{
          width: 38,
          height: 38,
          borderRadius: "19px",
          color: isDark ? "#F8F3EE" : colors.text.primary,
          border: isDark ? "1px solid rgba(212, 175, 55, 0.18)" : `1px solid ${colors.border.accent}`,
          backgroundColor: isDark ? "rgba(10, 8, 8, 0.75)" : colors.background.bar,
          boxShadow: isDark ? "inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 4px 12px rgba(0, 0, 0, 0.18)" : "0 2px 8px rgba(45, 33, 26, 0.08)",
          transition: "background-color 0.2s ease, border-color 0.2s ease",
          "&:hover": {
            backgroundColor: isDark ? "rgba(77, 17, 19, 0.72)" : colors.background.panelSecondary,
            borderColor: isDark ? "rgba(212, 175, 55, 0.45)" : colors.border.accent,
          },
        }}
      >
        <MenuRoundedIcon sx={{ fontSize: "18px" }} />
      </IconButton>

      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: "min(78vw, 300px)",
            p: "18px",
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            color: colors.text.primary,
            background: [
              "linear-gradient(135deg, rgba(255, 225, 132, 0.07) 0%, transparent 24%)",
              "linear-gradient(225deg, rgba(142, 29, 20, 0.20) 0%, transparent 42%)",
              `linear-gradient(180deg, ${brand.primaryDark} 0%, ${colors.background.page} 46%, ${brand.primarySoft} 100%)`,
            ].join(", "),
            borderRight: `1px solid ${colors.border.accent}`,
            boxShadow: "inset -1px 0 0 rgba(255, 225, 132, 0.10), 12px 0 32px rgba(20, 3, 3, 0.38)",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: "18px", gap: "12px" }}
        >
          <BrandLogo height={32} onClick={() => navigate("/")} />
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="close"
            sx={{ color: colors.text.secondary }}
          >
            <Typography sx={{ fontSize: "22px", lineHeight: 1 }}>×</Typography>
          </IconButton>
        </Stack>

        <Divider sx={{ mb: "20px", borderColor: colors.card.border }} />

        <Stack sx={{ gap: "10px" }}>
          {ACTIONS.map((action) => (
            <Button
              key={action.mode}
              onClick={() => choose(action.mode)}
              aria-current={action.mode === mode ? "page" : undefined}
              variant="outlinedDark"
              sx={actionSx(action.mode)}
            >
              {t(action.labelKey)}
            </Button>
          ))}
        </Stack>

        <Box sx={{ mt: "auto", pt: "18px", pb: "env(safe-area-inset-bottom)" }}>
          <Box sx={{ mb: "16px" }}>
            <SupportContact onClick={() => setOpen(false)} />
          </Box>
          <LanguageSwitcher />
        </Box>
      </Drawer>
    </>
  );
};

export default AuthNavDrawer;
